'use client'

import { Formik, Form } from 'formik'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2 } from 'lucide-react'
import { FormInput, FormTextarea } from '@/components/form-inputs'
import { AI_USE_CASES } from '@/lib/ai/langchain/types'
import type { AiUseCaseRoleView } from '@/server-actions/ai-use-case-roles'
import {
  useCreateAiUseCaseRoleMutation,
  useUpdateAiUseCaseRoleMutation,
} from '@/hooks/mutations/ai-use-case-roles'

interface RoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  useCaseSlug: string
  useCaseName: string
  /** null = create a new role for the use case. */
  editingRole: AiUseCaseRoleView | null
}

interface RoleFormValues {
  name: string
  description: string
  system_prompt: string
  /** Comma-separated categories (email_classification only). */
  categories: string
}

function parseCategories(raw: string): string[] {
  return raw
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean)
}

/**
 * Create/edit dialog for a system role of one use case. The slug is derived
 * from the name on create and immutable afterwards (agents store it inside
 * config.roles). email_classification roles additionally carry categories
 * (settings.classifier_labels).
 */
export function RoleDialog({
  open,
  onOpenChange,
  useCaseSlug,
  useCaseName,
  editingRole,
}: RoleDialogProps) {
  const createRole = useCreateAiUseCaseRoleMutation()
  const updateRole = useUpdateAiUseCaseRoleMutation()

  const hasCategories = useCaseSlug === AI_USE_CASES.EMAIL_CLASSIFICATION

  const initialValues: RoleFormValues = {
    name: editingRole?.name ?? '',
    description: editingRole?.description ?? '',
    system_prompt: editingRole?.system_prompt ?? '',
    categories: hasCategories
      ? ((editingRole?.settings.classifier_labels as string[] | undefined) ?? []).join(', ')
      : '',
  }

  const validate = (values: RoleFormValues) => {
    const errors: Partial<Record<keyof RoleFormValues, string>> = {}
    if (!values.name.trim()) errors.name = 'Name is required.'
    if (!values.system_prompt.trim()) errors.system_prompt = 'System prompt is required.'
    if (hasCategories && parseCategories(values.categories).length === 0) {
      errors.categories = 'At least one category is required.'
    }
    return errors
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingRole ? `Edit Role: ${editingRole.name}` : `Add Role — ${useCaseName}`}
          </DialogTitle>
          <DialogDescription>
            <span>Roles are presets applied when an agent is created: the system prompt</span>
            <span>{hasCategories ? ' and classification categories are' : ' is'}</span> <span>copied to the agent.</span>
          </DialogDescription>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validate={validate}
          onSubmit={async (values, { setStatus }) => {
            const settings = hasCategories
              ? { classifier_labels: parseCategories(values.categories) }
              : {}
            try {
              if (editingRole) {
                await updateRole.mutateAsync({
                  id: editingRole.id,
                  name: values.name.trim(),
                  description: values.description.trim() || null,
                  system_prompt: values.system_prompt.trim(),
                  settings,
                })
              } else {
                await createRole.mutateAsync({
                  use_case: useCaseSlug,
                  name: values.name.trim(),
                  description: values.description.trim() || null,
                  system_prompt: values.system_prompt.trim(),
                  settings,
                })
              }
              onOpenChange(false)
            } catch (error) {
              setStatus(error instanceof Error ? error.message : 'Failed to save role')
            }
          }}
        >
          {({ isSubmitting, status }) => (
            <Form className="space-y-4">
              {status && (
                <Alert variant="destructive">
                  <AlertDescription>{status}</AlertDescription>
                </Alert>
              )}

              <FormInput
                name="name"
                label="Name"
                placeholder="e.g., Support"
                maxLength={60}
                showLength
              />
              {editingRole && (
                <p className="text-xs text-muted-foreground">
                  <span>Slug:</span> <span className="font-mono">{editingRole.slug}</span> <span>(immutable — stored on
                  agents)</span>
                </p>
              )}

              <FormInput
                name="description"
                label="Description"
                placeholder="Short summary shown in the role selector"
                maxLength={300}
              />

              <FormTextarea
                name="system_prompt"
                label="Default System Prompt"
                placeholder="You are a customer support assistant..."
                rows={10}
                maxLength={20000}
                description="Copied to the agent when this role is selected."
              />

              {hasCategories && (
                <FormInput
                  name="categories"
                  label="Classification Categories"
                  placeholder="Support Request, Bug Report, Spam, Other"
                  description="Comma-separated categories the classifier must choose from."
                />
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <span>{editingRole ? 'Save Role' : 'Create Role'}</span>
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
